import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, TextField,
  FormControl, InputLabel, Select, MenuItem, Alert, CircularProgress,
  Autocomplete, Box, Tabs, Tab, InputAdornment, Checkbox, FormControlLabel, Paper,
  Typography
  
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import { apoliceService, Apolice, CreateApoliceDTO } from '../services/apoliceService';
import { pagamentoService, Pagamento } from '../services/pagamentoService';
import { ComissaoColaborador } from '../types/comissaoColaborador';
import { Cliente } from '../services/clienteService';
import { Colaborador, colaboradorService } from '../services/colaboradorService';
import { Empresa, empresaService } from '../services/empresaService';
import { useAuthContext } from '../contexts/AuthContext';

interface Ramo { id: number; nome: string; }
interface Seguradora { id: number; nome: string; }

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface ApoliceFormModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  apolice?: Apolice | null;
  clientePreSelecionado?: Cliente | null;
}

const ApoliceFormModal: React.FC<ApoliceFormModalProps> = ({ open, onClose, onSave, apolice, clientePreSelecionado }) => {
  const navigate = useNavigate();
  const { hasPermission } = useAuthContext();

  const [formData, setFormData] = useState<CreateApoliceDTO & { gerarParcelasAutomaticamente: boolean }>({
    numero: '', produto: '', status: 'Ativa', proposta: '', dataEmissao: '', dataVenda: '',
    seguradoraAnterior: undefined, apoliceAnterior: '', formaPagamento: undefined, propostaAssinada: false,
    apoliceConferida: false, dataInicio: '', dataFim: '', premioLiquido: 0, iof: 0,
    quantidadeParcelas: 1, comissaoVendedor: null, comissaoCorretora: null,
    cliente: { id: 0 }, seguradora: { id: 0 }, ramo: { id: 0 }, corretora: { id: 0 },
    premioBruto: '', dataPrimeiroPagamento: '', dataSegundoPagamento: '', gerarParcelasAutomaticamente: false,
  });

  const [ramos, setRamos] = useState<Ramo[]>([]);
  const [seguradoras, setSeguradoras] = useState<Seguradora[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [colaboradores, setColaboradores] = useState<Colaborador[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [pagamentos, setPagamentos] = useState<Pagamento[]>([]);
  const [comissoesColaborador, setComissoesColaborador] = useState<ComissaoColaborador[]>([]);

  const [loadingDependencies, setLoadingDependencies] = useState(false);
  const [clienteInput, setClienteInput] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: boolean }>({});

  const isNew = !apolice;

  useEffect(() => {
    const carregarDependencias = async () => {
      setLoadingDependencies(true);
      try {
        const [ramosData, seguradorasData, clientesData, colabsData, empresasData] = await Promise.all([
          api.get('/ramos'),
          api.get('/seguradoras'),
          api.get('/clientes', { params: { search: '', limit: 50 } }),
          colaboradorService.listar(),
          empresaService.listar(),
        ]);
        setRamos(ramosData.data);
        setSeguradoras(seguradorasData.data);
        setClientes(clientesData.data.data || []);
        setColaboradores(colabsData);
        setEmpresas(empresasData);
      } catch (error) {
        toast.error("Erro ao carregar dados para o formulário.");
      } finally {
        setLoadingDependencies(false);
      }
    };

    if (open) {
      carregarDependencias();
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setTabValue(0);
      setValidationErrors({});
      if (apolice) {
        setFormData({
          numero: apolice.numero,
          produto: apolice.produto || '',
          status: apolice.status,
          proposta: apolice.proposta || '',
          dataEmissao: apolice.dataEmissao?.split('T')[0] || '',
          dataVenda: apolice.dataVenda?.split('T')[0] || '',
          seguradoraAnterior: apolice.seguradoraAnterior ? { id: apolice.seguradoraAnterior.id } : undefined,
          apoliceAnterior: apolice.apoliceAnterior || '',
          formaPagamento: apolice.formaPagamento || undefined,
          propostaAssinada: apolice.propostaAssinada || false,
          apoliceConferida: apolice.apoliceConferida || false,
          dataInicio: apolice.dataInicio.split('T')[0],
          dataFim: apolice.dataFim.split('T')[0],
          premioLiquido: apolice.premioLiquido ?? 0,
          iof: apolice.iof,
          quantidadeParcelas: apolice.quantidadeParcelas,
          comissaoVendedor: apolice.comissaoVendedor ?? null,
          comissaoCorretora: apolice.comissaoCorretora ?? null,
          repasse: apolice.repasse,
          cliente: { id: apolice.cliente.id },
          seguradora: { id: apolice.seguradora.id },
          ramo: apolice.ramo ? { id: apolice.ramo.id } : { id: 0 },
          vendedor: apolice.vendedor ? { id: apolice.vendedor.id } : undefined,
          corretora: apolice.corretora ? { id: apolice.corretora.id } : undefined,
          premioBruto: apolice.premioBruto ?? '',
          dataPrimeiroPagamento: apolice.dataPrimeiroPagamento ? apolice.dataPrimeiroPagamento.split('T')[0] : '',
          dataSegundoPagamento: apolice.dataSegundoPagamento ? apolice.dataSegundoPagamento.split('T')[0] : '',
          gerarParcelasAutomaticamente: false,
        });
      } else {
        // Reset e pré-seleciona o cliente se fornecido
        setFormData({
          numero: '', produto: '', status: 'Ativa', proposta: '', dataEmissao: '', dataVenda: '',
          seguradoraAnterior: undefined, apoliceAnterior: '', formaPagamento: undefined, propostaAssinada: false,
          apoliceConferida: false, dataInicio: '', dataFim: '', premioLiquido: 0, iof: 0,
          quantidadeParcelas: 1, comissaoVendedor: null, comissaoCorretora: null,
          cliente: { id: clientePreSelecionado?.id || 0 },
          seguradora: { id: 0 }, ramo: { id: 0 }, corretora: { id: 0 },
          premioBruto: '', dataPrimeiroPagamento: '', dataSegundoPagamento: '', gerarParcelasAutomaticamente: false,
        });
        if (clientePreSelecionado && !clientes.some(c => c.id === clientePreSelecionado.id)) {
            setClientes(prev => [clientePreSelecionado, ...prev]);
        }
      }
    }
  }, [open, apolice, clientePreSelecionado, clientes]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    if (isNew && newValue !== 0) {
      return;
    }
    setTabValue(newValue);
  };

  const clearFieldError = (fieldName: string) => {
    if (validationErrors[fieldName]) {
      setValidationErrors(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleSubmit = async () => {
    try {
      if (isNew) {
        const erros: string[] = [];
        const novosErros: { [key: string]: boolean } = {};
        
        if (!formData.cliente.id) { erros.push('Cliente é obrigatório'); novosErros.cliente = true; }
        if (!formData.seguradora.id) { erros.push('Seguradora é obrigatória'); novosErros.seguradora = true; }
        if (!formData.ramo.id) { erros.push('Ramo é obrigatório'); novosErros.ramo = true; }
        if (!formData.vendedor?.id) { erros.push('Vendedor é obrigatório'); novosErros.vendedor = true; }
        if (!formData.corretora?.id) { erros.push('Corretora é obrigatória'); novosErros.corretora = true; }
        if (!formData.dataVenda) { erros.push('Data da venda é obrigatória'); novosErros.dataVenda = true; }
        if (!formData.dataInicio) { erros.push('Data de início é obrigatória'); novosErros.dataInicio = true; }
        if (!formData.dataFim) { erros.push('Data de fim é obrigatória'); novosErros.dataFim = true; }
        
        setValidationErrors(novosErros);
        if (erros.length > 0) {
          erros.forEach(erro => toast.error(erro));
          return;
        }

        const novaApolice = await apoliceService.criar({
          ...formData,
          premioBruto: 0, iof: 0, comissaoVendedor: 0, comissaoCorretora: 0,
          quantidadeParcelas: 1, premioLiquido: 0,
        });
        
        toast.success('Apólice criada com sucesso! Redirecionando para edição...');
        setTimeout(() => {
          onSave();
          navigate(`/apolices/editar/${novaApolice.id}`);
        }, 1500);

      } else {
        // Lógica de atualização (se necessário no futuro)
        toast.info("A edição completa deve ser feita na página de edição da apólice.");
      }
    } catch (error) {
      toast.error('Erro ao salvar apólice.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{isNew ? 'Nova Apólice - Dados Gerais' : 'Editar Apólice'}</DialogTitle>
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Dados Gerais" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          {isNew && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Preencha os dados gerais. Os demais detalhes (prêmios, pagamentos, etc.) poderão ser adicionados na tela de edição da apólice.
            </Alert>
          )}
          {loadingDependencies ? <CircularProgress /> : (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  options={clientes}
                  getOptionLabel={(option) => option.nome.toUpperCase()}
                  value={clientes.find(c => c.id === formData.cliente.id) || null}
                  onChange={(_, newValue) => {
                    setFormData({ ...formData, cliente: { id: newValue ? newValue.id : 0 } });
                    clearFieldError('cliente');
                  }}
                  onInputChange={(_, newInputValue) => setClienteInput(newInputValue)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  disabled={!!clientePreSelecionado}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cliente *"
                      error={validationErrors.cliente}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth error={validationErrors.seguradora}>
                  <InputLabel>Seguradora *</InputLabel>
                  <Select
                    value={formData.seguradora.id}
                    label="Seguradora *"
                    onChange={(e) => {
                      setFormData({ ...formData, seguradora: { id: e.target.value as number } });
                      clearFieldError('seguradora');
                    }}
                  >
                    <MenuItem value={0}>Selecione</MenuItem>
                    {seguradoras.map((s) => <MenuItem key={s.id} value={s.id}>{s.nome}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth error={validationErrors.ramo}>
                  <InputLabel>Ramo da Apólice *</InputLabel>
                  <Select
                    value={formData.ramo.id}
                    label="Ramo da Apólice *"
                    onChange={(e) => {
                      setFormData({ ...formData, ramo: { id: e.target.value as number } });
                      clearFieldError('ramo');
                    }}
                  >
                    <MenuItem value={0}>Selecione</MenuItem>
                    {ramos.map((r) => <MenuItem key={r.id} value={r.id}>{r.nome}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth error={validationErrors.vendedor}>
                  <InputLabel>Vendedor *</InputLabel>
                  <Select
                    value={formData.vendedor?.id || 0}
                    label="Vendedor *"
                    onChange={(e) => {
                      setFormData({ ...formData, vendedor: { id: e.target.value as number } });
                      clearFieldError('vendedor');
                    }}
                  >
                    <MenuItem value={0}>Selecione</MenuItem>
                    {colaboradores.map((c) => <MenuItem key={c.id} value={c.id}>{c.nome.toUpperCase()}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth error={validationErrors.corretora}>
                  <InputLabel>Corretora *</InputLabel>
                  <Select
                    value={formData.corretora?.id || 0}
                    label="Corretora *"
                    onChange={(e) => {
                      setFormData({ ...formData, corretora: { id: e.target.value as number } });
                      clearFieldError('corretora');
                    }}
                  >
                    <MenuItem value={0}>Selecione</MenuItem>
                    {empresas.map((e) => <MenuItem key={e.id} value={e.id}>{e.nome}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Número da Apólice"
                  value={formData.numero}
                  onChange={(e) => setFormData({ ...formData, numero: e.target.value.toUpperCase() })}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Produto"
                  value={formData.produto}
                  onChange={(e) => setFormData({ ...formData, produto: e.target.value.toUpperCase() })}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Status *</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status *"
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as 'Ativa' | 'Cancelada' | 'Vencida' | 'Renovação' })}
                  >
                    <MenuItem value="Ativa">Ativa</MenuItem>
                    <MenuItem value="Cancelada">Cancelada</MenuItem>
                    <MenuItem value="Vencida">Vencida</MenuItem>
                    <MenuItem value="Renovação">Renovação</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Proposta"
                  value={formData.proposta}
                  onChange={(e) => setFormData({ ...formData, proposta: e.target.value.toUpperCase() })}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Data de Emissão"
                  value={formData.dataEmissao}
                  onChange={(e) => setFormData({ ...formData, dataEmissao: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Data da Venda *"
                  value={formData.dataVenda}
                  onChange={(e) => {
                    setFormData({ ...formData, dataVenda: e.target.value });
                    clearFieldError('dataVenda');
                  }}
                  error={validationErrors.dataVenda}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Seguradora Anterior</InputLabel>
                  <Select
                    value={formData.seguradoraAnterior?.id || 0}
                    label="Seguradora Anterior"
                    onChange={(e) => setFormData({ ...formData, seguradoraAnterior: { id: e.target.value as number } })}
                  >
                    <MenuItem value={0}>Selecione</MenuItem>
                    {seguradoras.map((s) => <MenuItem key={s.id} value={s.id}>{s.nome}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Apólice Anterior"
                  value={formData.apoliceAnterior}
                  onChange={(e) => setFormData({ ...formData, apoliceAnterior: e.target.value.toUpperCase() })}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="CI (Opcional)"
                  name="ci"
                  type="text"
                  value={formData.ci || ''}
                  onChange={(e) => setFormData({ ...formData, ci: e.target?.value ? e.target.value : undefined })}
                  fullWidth
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Forma de Pagamento</InputLabel>
                  <Select
                    value={formData.formaPagamento || ''}
                    label="Forma de Pagamento"
                    onChange={(e) => setFormData({ ...formData, formaPagamento: e.target.value as 'CRÉDITO' | 'DÉBITO' | 'PIX' | 'BOLETO' })}
                  >
                    <MenuItem value="">Selecione</MenuItem>
                    <MenuItem value="CRÉDITO">CRÉDITO</MenuItem>
                    <MenuItem value="DÉBITO">DÉBITO</MenuItem>
                    <MenuItem value="PIX">PIX</MenuItem>
                    <MenuItem value="BOLETO">BOLETO</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Proposta Assinada</InputLabel>
                  <Select
                    value={formData.propostaAssinada ? 'true' : 'false'}
                    label="Proposta Assinada"
                    onChange={(e) => setFormData({ ...formData, propostaAssinada: e.target.value === 'true' })}
                  >
                    <MenuItem value="true">Sim</MenuItem>
                    <MenuItem value="false">Não</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Apólice Conferida</InputLabel>
                  <Select
                    value={formData.apoliceConferida ? 'true' : 'false'}
                    label="Apólice Conferida"
                    onChange={(e) => setFormData({ ...formData, apoliceConferida: e.target.value === 'true' })}
                  >
                    <MenuItem value="true">Sim</MenuItem>
                    <MenuItem value="false">Não</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" sx={{ mb: 2, mt: 2 }}>Datas de Vigência</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Data de Início *"
                  value={formData.dataInicio}
                  onChange={(e) => {
                    setFormData({ ...formData, dataInicio: e.target.value });
                    clearFieldError('dataInicio');
                  }}
                  error={validationErrors.dataInicio}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Data de Fim *"
                  value={formData.dataFim}
                  onChange={(e) => {
                    setFormData({ ...formData, dataFim: e.target.value });
                    clearFieldError('dataFim');
                  }}
                  error={validationErrors.dataFim}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          )}
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={handleSubmit} variant="contained">
          {isNew ? 'Criar e Ir para Edição' : 'Atualizar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApoliceFormModal;